import React from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import pick from './utilities/pick'
import mapDispatchers from './utilities/mapDispatchers'
import EmailInputAndValidation from './components/EmailInputAndValidation'
import styles from './CompleteEmailLogin.icss'
import getStartLocationForUser from './utilities/getStartLocationForUser'
import deviceDetect from './utilities/deviceDetect'
import EmailRedirectUrlInputAndValidation from './components/EmailRedirectUrlInputAndValidation'
import messageTypes from './enums/messageTypes'

const EMAIL_FOR_LOGIN = 'emailForLogin'
const handleUrlSubmit = (url) => {
	global.location.href = url
}
let hasCalledAuthEmailLoginComplete = false

class CompleteEmailLogin extends React.Component {
	constructor(props) {
		super(props)

		this.state = { email: global.localStorage.getItem(EMAIL_FOR_LOGIN) || '' }

		this.handleEmail = this.handleEmail.bind(this)
		this.handleLogin = this.handleLogin.bind(this)
		//	On iOS using instagram or pwa, we need to paste the url in when using email.
		this.needsUrlPasted = deviceDetect.isIos() && (deviceDetect.isInstagram() || deviceDetect.isPwa())
	}

	componentDidMount() {
		this.props.appBackButtonFunction(() => this.props.appRedirect('/login'))
	}

	componentDidUpdate() {
		// Start the login complete process once the initial auth user is loaded (anonymous or otherwise...)
		if (!this.props.user.isLoading && !hasCalledAuthEmailLoginComplete) {
			hasCalledAuthEmailLoginComplete = true
			this.props.authEmailLoginComplete()
		}
		// See if the user actually got logged in, or was already logged in previously.
		if (
			!this.props.user.isAnonymous && !this.props.user.isLoading &&
			(
				(this.props.authEmailLoginCompleteSuccess && !this.props.authEmailLoginCompleteEmailAddressRequired) ||
				this.props.authEmailLoginAlreadyComplete
			)
		) {
			this.props.appRedirect(getStartLocationForUser(this.props.user))
			if (this.props.authEmailLoginAlreadyComplete) {
				this.props.appMessage(messageTypes.POSITIVE, this.context.translate('Welcome back'))
			}
		}
	}

	handleEmail(email) {
		this.props.authEmailLoginComplete({ email })
		this.setState({ email })
	}

	handleLogin(email) {
		this.props.authEmailLogin({ email })
		this.setState({ email })
	}

	render() {
		const emailRequired = this.props.authEmailLoginCompleteEmailAddressRequired && !this.props.authEmailLoginCompleteFail
		return (
			<section className={styles.surround}>
				<div className={classNames(styles.container, { [styles.fullPageHeight]: this.props.authEmailLoginCompleteEmailAddressRequired || this.props.authEmailLoginCompleteFail || this.props.authEmailLoginSuccess })}>
					{!this.props.authEmailLoginSuccess && (
						<>
							<h2>{this.context.translate('Completing email login')}</h2>

							{emailRequired && (
								<>
									<p>{this.context.translate('Did you login from a different browser? You’ll need to type your email address below')}</p>

									<EmailInputAndValidation
										emailHandler={this.handleEmail}
										label={this.context.translate('Enter email address')}
										buttonText={this.context.translate('Complete login process')}
									/>
								</>
							)}

							{this.props.authEmailLoginCompleteFail && (
								<>
									<p>{this.props.authEmailLoginCompleteError || this.context.translate('Login failed.')} {this.context.translate('Get a new login link sent to your email:')}</p>

									<EmailInputAndValidation
										fullWidth
										emailHandler={this.handleLogin}
										label={this.context.translate('E-mail address')}
										buttonText={this.context.translate('Login Again')}
										email={this.state.email}
									/>
									{this.props.authEmailLoginFail && (
										<div>
											<span className={styles.validationError}>{this.props.authEmailLoginError ? this.props.authEmailLoginError : this.context.translate('Please enter a valid email')}</span>
										</div>
									)}
								</>
							)}
						</>
					)}

					{this.props.authEmailLoginSuccess && (
						<>
							<h2>{this.context.translate('Check Your Email')}</h2>

							{this.needsUrlPasted && (
								<>
									<p>
										{this.context.translate(`We've sent a login link to your email (${this.state.email}), once it arrives copy it and paste it in below.`)}
									</p>
									<EmailRedirectUrlInputAndValidation
										fullWidth
										urlHandler={handleUrlSubmit}
										label={this.context.translate('Login Url')}
										buttonText={this.context.translate('Submit')}
									/>
								</>
							)}
							{!this.needsUrlPasted && (
								<>
									<p>
										{this.context.translate(`We've sent a login link to your email (${this.state.email}). Click it and you’ll be signed in.`)}
									</p>
									<p>
										{this.context.translate('NB: We’ll try to keep you logged in but if you come back and need to login again just repeat this process. Easy peasy!')}
									</p>
								</>
							)}
						</>
					)}
				</div>
			</section>
		)
	}
}

CompleteEmailLogin.propTypes = {
	appBackButtonFunction: PropTypes.func.isRequired,
	appMessage: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	authEmailLogin: PropTypes.func.isRequired,
	authEmailLoginError: PropTypes.string,
	authEmailLoginFail: PropTypes.bool,
	authEmailLoginSuccess: PropTypes.bool,
	authEmailLoginAlreadyComplete: PropTypes.bool,
	authEmailLoginComplete: PropTypes.func.isRequired,
	authEmailLoginCompleteEmailAddressRequired: PropTypes.bool,
	authEmailLoginCompleteError: PropTypes.string,
	authEmailLoginCompleteFail: PropTypes.bool,
	authEmailLoginCompleteSuccess: PropTypes.bool,
	user: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types
}

CompleteEmailLogin.contextTypes = {
	translate: PropTypes.func
}

CompleteEmailLogin.defaultProps = {
	authEmailLoginAlreadyComplete: false,
	authEmailLoginCompleteEmailAddressRequired: false,
	authEmailLoginCompleteError: undefined,
	authEmailLoginCompleteFail: false,
	authEmailLoginCompleteSuccess: false,
	authEmailLoginError: undefined,
	authEmailLoginFail: false,
	authEmailLoginSuccess: false
}

export const testing = {
	CompleteEmailLogin
}

export default connect(
	/* istanbul ignore next line */
	state => pick(state, ['user', 'authEmailLoginCompleteEmailAddressRequired', 'authEmailLoginAlreadyComplete', 'authEmailLoginCompleteSuccess', 'authEmailLoginCompleteFail', 'authEmailLoginCompleteError', 'authEmailLoginError', 'authEmailLoginFail', 'authEmailLoginSuccess']),
	/* istanbul ignore next line */
	dispatch => mapDispatchers(dispatch, ['appMessage', 'appRedirect', 'appBackButtonFunction', 'authEmailLogin', 'authEmailLoginComplete'])
)(CompleteEmailLogin)



// WEBPACK FOOTER //
// ./src/CompleteEmailLogin.jsx